import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "reconciliation-history";
type History = Record<string, number>;

function readHistory(): History {
  try {
    const value: unknown = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) ?? "{}",
    );
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(
        ([, timestamp]) =>
          typeof timestamp === "number" &&
          Number.isFinite(timestamp) &&
          timestamp > 0 &&
          timestamp <= Date.now(),
      ),
    );
  } catch {
    return {};
  }
}

// Group IDs identify the tenant's groups; user IDs keep accounts separate.
export function useReconciliationHistory(userId: string, visible: boolean) {
  const [history, setHistory] = useState<History>(readHistory);
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch {
      // Keep the in-memory history if storage is unavailable or full.
    }
  }, [history]);

  useEffect(() => {
    if (!visible) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, [visible]);

  const recordCompletion = useCallback(
    (groupId: string) => {
      const timestamp = Date.now();
      setHistory((current) => ({
        ...current,
        [`${userId}:${groupId}`]: timestamp,
      }));
      setNow(timestamp);
    },
    [userId],
  );

  return {
    lastCompletedAt: (groupId: string) => history[`${userId}:${groupId}`],
    recordCompletion,
    now,
  };
}
